import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth, adminApi } from "../contexts/AdminAuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  LogOut, Search, Lock, Unlock, Edit, Trash2, AlertTriangle, ShieldAlert,
  Users, Briefcase, Activity as ActivityIcon, History, Copy, Clock, CalendarClock,
  LogIn, UserCog, RefreshCw,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { admin, logout, loading } = useAdminAuth();
  const [clubs, setClubs] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [audit, setAudit] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [editForm, setEditForm] = useState({});
  const [suspTarget, setSuspTarget] = useState(null);
  const [suspForm, setSuspForm] = useState({ until: "", reason: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [pending, setPending] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!loading && !admin) navigate("/admin");
  }, [admin, loading, navigate]);

  const load = async () => {
    const [c, d, a, s, p] = await Promise.all([
      adminApi.get("/admin/clubs", { params: search ? { q: search } : {} }).then(r => r.data),
      adminApi.get("/admin/duplicates").then(r => r.data).catch(() => []),
      adminApi.get("/admin/audit", { params: { limit: 30 } }).then(r => r.data).catch(() => []),
      adminApi.get("/admin/status").then(r => r.data).catch(() => null),
      adminApi.get("/admin/pending-clubs").then(r => r.data).catch(() => []),
    ]);
    setClubs(c); setDuplicates(d); setAudit(a); setStatusData(s); setPending(p);
  };
  useEffect(() => { if (admin) load(); }, [admin, search]);

  const handleLock = async (club, lock) => {
    const word = lock ? "verrouiller" : "déverrouiller";
    if (!window.confirm(`Êtes-vous sûr de vouloir ${word} ${club.name} ?`)) return;
    try {
      await adminApi.patch(`/admin/clubs/${club.id}/lock`, null, { params: { lock } });
      toast.success(`Club ${lock ? "verrouillé" : "déverrouillé"}`);
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  const openEdit = (c) => {
    setEditTarget(c);
    setEditForm({ name: c.name, slug: c.slug, city: c.city || "", country: c.country, sport: c.sport || "", subtitle: c.subtitle || "" });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.patch(`/admin/clubs/${editTarget.id}`, editForm);
      toast.success("Club modifié");
      setEditTarget(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const confirmDelete = async () => {
    if (delConfirm !== delTarget.name) {
      toast.error("Le nom ne correspond pas");
      return;
    }
    try {
      await adminApi.delete(`/admin/clubs/${delTarget.id}`);
      toast.success(`Club ${delTarget.name} supprimé définitivement`);
      setDelTarget(null); setDelConfirm("");
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  const openSuspend = (c) => {
    // Default: +24h
    const next = new Date(Date.now() + 24 * 3600 * 1000);
    next.setSeconds(0, 0);
    const iso = next.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    setSuspTarget(c);
    setSuspForm({ until: iso, reason: "" });
  };

  const submitSuspend = async (e) => {
    e.preventDefault();
    try {
      await adminApi.patch(`/admin/clubs/${suspTarget.id}/suspend`, {
        until: new Date(suspForm.until).toISOString(),
        reason: suspForm.reason,
      });
      toast.success(`${suspTarget.name} suspendu jusqu'au ${new Date(suspForm.until).toLocaleString("fr-FR")}`);
      setSuspTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const liftSuspension = async (c) => {
    if (!window.confirm(`Lever immédiatement la suspension de ${c.name} ?`)) return;
    try {
      await adminApi.delete(`/admin/clubs/${c.id}/suspend`);
      toast.success("Suspension levée");
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  const handleLogout = async () => { await logout(); navigate("/admin"); };

  // ───── Refresh helpers ─────────────────────────────────────────────
  const refreshAll = async () => {
    setRefreshingAll(true);
    try {
      await load();
      toast.success("Données rafraîchies");
    } catch (_) {
      toast.error("Erreur lors du rafraîchissement");
    } finally {
      setRefreshingAll(false);
    }
  };

  const refreshOne = async (c) => {
    setRefreshingId(c.id);
    try {
      const { data } = await adminApi.get(`/admin/clubs/${c.id}/refresh`);
      setClubs((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
      toast.success(`${c.name} actualisé`);
    } catch (_) {
      toast.error("Erreur");
    } finally {
      setRefreshingId(null);
    }
  };

  const fmtActivity = (iso) => {
    if (!iso) return "Jamais connecté";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 7) return `Il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR");
  };

  // ───── Pending registration approval / rejection ─────────────────
  const approvePending = async (c) => {
    if (!window.confirm(`Approuver l'inscription de "${c.name}" ?\n\nLe club sera activé et pourra se connecter. Un email de notification sera envoyé.`)) return;
    try {
      await adminApi.post(`/admin/clubs/${c.id}/approve`);
      toast.success(`${c.name} approuvé`);
      setReviewing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };
  const rejectPending = async () => {
    if (!reviewing) return;
    try {
      await adminApi.post(`/admin/clubs/${reviewing.id}/reject`, {
        reason: rejectReason.trim() || "Document d'inscription non conforme",
      });
      toast.success(`${reviewing.name} refusé`);
      setReviewing(null); setRejectReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };
  const adminFileUrl = (path) => {
    const token = localStorage.getItem("asc_admin_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/pending-files/${path}?auth=${token}`;
  };

  // ───── Bulk selection ──────────────────────────────────────────────
  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const allSelected = clubs.length > 0 && clubs.every((c) => selected.has(c.id));
  const toggleAll = () => {
    setSelected((s) => {
      if (allSelected) return new Set();
      return new Set(clubs.map((c) => c.id));
    });
  };
  const clearSelection = () => setSelected(new Set());

  const BULK_CONFIRM_TOKEN = `SUPPRIMER ${selected.size}`;
  const submitBulkDelete = async () => {
    if (bulkConfirm !== BULK_CONFIRM_TOKEN) {
      toast.error("Confirmation incorrecte");
      return;
    }
    try {
      const ids = Array.from(selected);
      const { data } = await adminApi.post("/admin/clubs/bulk-delete", { club_ids: ids });
      toast.success(`${data.count} club${data.count > 1 ? "s" : ""} supprimé${data.count > 1 ? "s" : ""}`);
      setBulkDelOpen(false); setBulkConfirm(""); clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // ───── Impersonation ───────────────────────────────────────────────
  const impersonate = async (c) => {
    const ok = window.confirm(
      `Vous allez accéder à l'interface de "${c.name}" en mode SUPPORT.\n\n` +
      `• Vous y entrerez en tant que Président (session 4h)\n` +
      `• Toutes vos actions sont consignées dans le journal d'audit\n` +
      `• Un bandeau jaune apparaîtra en haut de l'interface\n\n` +
      `Continuer ?`
    );
    if (!ok) return;
    try {
      const { data } = await adminApi.post(`/admin/clubs/${c.id}/impersonate`);
      localStorage.setItem("asc_token", data.token);
      toast.success(`Accès à ${c.name}`);
      // Full reload so AuthProvider picks up the new token cleanly
      window.location.href = "/app";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading || !admin) return <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center text-white">Chargement…</div>;

  const total = clubs.length;
  const active = clubs.filter(c => c.is_active).length;
  const locked = total - active;
  const totalPlayers = clubs.reduce((s, c) => s + (c.stats?.players || 0), 0);

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <header className="border-b border-white/10 bg-[#161616]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FCD116]/15 border border-[#FCD116]/40 flex items-center justify-center">
              <ShieldAlert className="w-4 h-4 text-[#FCD116]" />
            </div>
            <div>
              <p className="font-display font-bold text-sm">Panel Propriétaire</p>
              <p className="text-[10px] tracking-overline text-white/45">WSDK Connect · Super Admin</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/5" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <p className="text-xs tracking-overline text-[#FCD116] mb-2">Vue d'ensemble</p>
        <h1 className="font-display text-4xl font-extrabold mb-8">Gestion de la plateforme</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KPI icon={Users} label="Clubs total" value={total} color="#FCD116" testid="kpi-total" />
          <KPI icon={ActivityIcon} label="Actifs" value={active} color="#2E7D32" testid="kpi-active" />
          <KPI icon={Lock} label="Verrouillés" value={locked} color="#C62828" testid="kpi-locked" />
          <KPI icon={Briefcase} label="Joueurs total" value={totalPlayers} color="#1976D2" testid="kpi-players" />
        </div>

        <Tabs defaultValue="clubs" className="space-y-4">
          <TabsList className="bg-[#161616] border border-white/10">
            <TabsTrigger value="clubs" data-testid="tab-admin-clubs">Clubs</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-admin-pending" className="relative">
              Demandes
              {pending.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FBC02D] text-[#121212] text-[10px] font-bold">
                  {pending.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="tab-admin-duplicates">
              Doublons {duplicates.length > 0 && <Badge variant="outline" className="ml-2 bg-[#C62828]/20 text-[#C62828] border-[#C62828]/40">{duplicates.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="status" data-testid="tab-admin-status">État du système</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-admin-audit">Journal d'audit</TabsTrigger>
          </TabsList>

          <TabsContent value="clubs">
            <div className="bg-[#161616] border border-white/10 rounded-md p-4 mb-3 flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un club..." className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  data-testid="admin-clubs-search" />
              </div>
              <Button onClick={refreshAll} disabled={refreshingAll}
                className="bg-[#FCD116] hover:bg-[#E8BC0F] text-[#121212] font-semibold flex-shrink-0"
                data-testid="admin-refresh-all-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingAll ? "animate-spin" : ""}`} />
                Actualiser tout
              </Button>
            </div>

            {selected.size > 0 && (
              <div className="bg-[#C62828]/15 border border-[#C62828]/40 rounded-md p-3 mb-3 flex items-center justify-between" data-testid="admin-bulk-bar">
                <p className="text-sm">
                  <strong className="text-white">{selected.size}</strong>
                  <span className="text-white/70"> club{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}</span>
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={clearSelection} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="admin-bulk-clear">
                    Tout désélectionner
                  </Button>
                  <Button size="sm" onClick={() => { setBulkConfirm(""); setBulkDelOpen(true); }} className="bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="admin-bulk-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer la sélection
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-[#161616] border border-white/10 rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-clubs-table">
                  <thead className="bg-white/5 text-white/55 tracking-overline text-[10px]">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          className="border-white/30 data-[state=checked]:bg-[#FCD116] data-[state=checked]:text-[#121212]"
                          aria-label="Tout sélectionner"
                          data-testid="admin-select-all"
                        />
                      </th>
                      <th className="text-left px-4 py-3">Club</th>
                      <th className="text-left px-4 py-3">Slug</th>
                      <th className="text-left px-4 py-3">Pays</th>
                      <th className="text-right px-4 py-3">Effectifs</th>
                      <th className="text-left px-4 py-3">État</th>
                      <th className="text-left px-4 py-3">Dernière activité</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {clubs.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-white/45">Aucun club.</td></tr>
                    ) : clubs.map((c) => (
                      <tr key={c.id} className={`hover:bg-white/5 ${selected.has(c.id) ? "bg-[#FCD116]/5" : ""}`} data-testid={`admin-club-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selected.has(c.id)}
                            onCheckedChange={() => toggleOne(c.id)}
                            className="border-white/30 data-[state=checked]:bg-[#FCD116] data-[state=checked]:text-[#121212]"
                            aria-label={`Sélectionner ${c.name}`}
                            data-testid={`admin-select-${c.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold">{c.name}</p>
                          <p className="text-[11px] text-white/45">{c.city || "—"}</p>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-white/65">{c.slug}</td>
                        <td className="px-4 py-3 text-white/75">{c.country}</td>
                        <td className="px-4 py-3 text-right text-xs">
                          <span className="font-mono">{c.stats?.players ?? 0}</span> j ·{" "}
                          <span className="font-mono">{c.stats?.teams ?? 0}</span> éq ·{" "}
                          <span className="font-mono">{c.stats?.employees ?? 0}</span> emp
                        </td>
                        <td className="px-4 py-3">
                          {c.suspended_until ? (
                            <div>
                              <Badge variant="outline" className="bg-[#F57F17]/15 text-[#F57F17] border-[#F57F17]/40">
                                <Clock className="w-3 h-3 mr-1" /> Suspendu
                              </Badge>
                              <p className="text-[10px] text-white/55 mt-1 max-w-[180px] truncate" title={c.suspension_reason}>
                                Jusqu'au {new Date(c.suspended_until).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                              </p>
                            </div>
                          ) : c.is_active ? (
                            <Badge variant="outline" className="bg-[#2E7D32]/15 text-[#2E7D32] border-[#2E7D32]/40">Actif</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-[#C62828]/15 text-[#C62828] border-[#C62828]/40">Verrouillé</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-white/70" data-testid={`admin-last-activity-${c.id}`}>
                          {fmtActivity(c.last_activity)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="ghost" onClick={() => refreshOne(c)} disabled={refreshingId === c.id} className="text-white/70 hover:text-[#FCD116] hover:bg-white/5" title="Actualiser ce club" data-testid={`admin-refresh-${c.id}`}>
                            <RefreshCw className={`w-4 h-4 ${refreshingId === c.id ? "animate-spin" : ""}`} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => impersonate(c)} className="text-white/70 hover:text-[#FCD116] hover:bg-white/5" title="Accéder à l'interface du club (mode support)" data-testid={`admin-impersonate-${c.id}`}>
                            <UserCog className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid={`admin-edit-${c.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {c.suspended_until ? (
                            <Button size="sm" variant="ghost" onClick={() => liftSuspension(c)} className="text-[#F57F17] hover:text-white hover:bg-white/5" title="Lever la suspension" data-testid={`admin-lift-${c.id}`}>
                              <CalendarClock className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => openSuspend(c)} className="text-white/70 hover:text-[#F57F17] hover:bg-white/5" title="Suspendre temporairement" data-testid={`admin-suspend-${c.id}`}>
                              <Clock className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleLock(c, c.is_active)} className="text-white/70 hover:text-white hover:bg-white/5" data-testid={`admin-lock-${c.id}`}>
                            {c.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4 text-[#FCD116]" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDelTarget(c)} className="text-white/70 hover:text-[#C62828] hover:bg-white/5" data-testid={`admin-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pending">
            {pending.length === 0 ? (
              <div className="bg-[#161616] border border-white/10 rounded-md p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-[#2E7D32]/15 mx-auto flex items-center justify-center mb-3">
                  <ShieldAlert className="w-5 h-5 text-[#2E7D32]" />
                </div>
                <p className="font-display text-lg font-bold">Aucune demande en attente</p>
                <p className="text-sm text-white/55 mt-1">Les nouvelles inscriptions apparaîtront ici pour validation.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-pending-list">
                {pending.map((c) => (
                  <div key={c.id} className="bg-[#161616] border border-[#FCD116]/40 rounded-md p-5" data-testid={`admin-pending-${c.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="bg-[#FCD116]/15 text-[#FCD116] border-[#FCD116]/40">En attente</Badge>
                          <p className="font-display text-lg font-bold">{c.name}</p>
                        </div>
                        <p className="text-xs text-white/55 font-mono">{c.slug}</p>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-xs">
                          <div>
                            <p className="tracking-overline text-white/45 text-[10px]">Pays</p>
                            <p className="font-medium">{c.country}{c.city ? ` · ${c.city}` : ""}</p>
                          </div>
                          <div>
                            <p className="tracking-overline text-white/45 text-[10px]">Sport</p>
                            <p className="font-medium">{c.sport || "—"}</p>
                          </div>
                          <div>
                            <p className="tracking-overline text-white/45 text-[10px]">Email président</p>
                            <p className="font-medium truncate" title={c.contact_email}>{c.contact_email}</p>
                          </div>
                          <div>
                            <p className="tracking-overline text-white/45 text-[10px]">Téléphone</p>
                            <p className="font-medium">{c.contact_phone}</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-white/45 mt-2">Reçu le {new Date(c.created_at).toLocaleString("fr-FR")}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {c.recipisse_url && (
                          <a
                            href={adminFileUrl(c.recipisse_url)}
                            target="_blank" rel="noopener noreferrer"
                            className="px-3 py-2 rounded border border-white/15 hover:border-white/30 text-xs flex items-center gap-1.5"
                            data-testid={`admin-pending-doc-${c.id}`}
                          >
                            <Copy className="w-3.5 h-3.5" /> Voir le récépissé
                          </a>
                        )}
                        <Button onClick={() => approvePending(c)} className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white" data-testid={`admin-approve-${c.id}`}>
                          Approuver
                        </Button>
                        <Button onClick={() => { setReviewing(c); setRejectReason(""); }} variant="outline" className="border-[#C62828]/40 text-[#C62828] hover:bg-[#C62828]/10" data-testid={`admin-reject-${c.id}`}>
                          Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="duplicates">
            <div className="bg-[#161616] border border-white/10 rounded-md p-6">
              {duplicates.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-full bg-[#2E7D32]/15 mx-auto flex items-center justify-center mb-3">
                    <ActivityIcon className="w-5 h-5 text-[#2E7D32]" />
                  </div>
                  <p className="font-display text-lg font-bold">Aucun doublon détecté</p>
                  <p className="text-sm text-white/55 mt-1">Tous les clubs ont des noms uniques.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="admin-duplicates-list">
                  {duplicates.map((g) => (
                    <div key={g._id} className="border border-[#FCD116]/30 rounded-md p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-[#FCD116]" />
                        <p className="font-semibold">« {g._id} » — {g.count} clubs identiques</p>
                      </div>
                      <div className="space-y-2">
                        {g.clubs.map((c) => (
                          <div key={c.id} className="flex items-center justify-between bg-white/5 rounded p-3 text-sm">
                            <div>
                              <p className="font-medium">{c.name}</p>
                              <p className="text-xs text-white/55">{c.slug} · {c.city || "—"} · {c.stats?.players ?? 0} joueurs · créé {c.created_at?.slice(0, 10)}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => {
                                const full = clubs.find(x => x.id === c.id) || c;
                                openEdit(full);
                              }} className="text-white/70 hover:text-white"><Edit className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => setDelTarget({ ...c, name: c.name })} className="text-white/70 hover:text-[#C62828]">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="status">
            <div className="bg-[#161616] border border-white/10 rounded-md p-6" data-testid="admin-status-content">
              {!statusData ? (
                <p className="text-white/55 text-sm">Chargement...</p>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2E7D32] animate-pulse" />
                    <div>
                      <p className="font-display text-xl font-bold">Tous les services sont opérationnels</p>
                      <p className="text-xs text-white/55 mt-0.5">
                        Version {statusData.version} · Démarré le {new Date(statusData.started_at).toLocaleString("fr-FR")} · Uptime {formatUptime(statusData.uptime_seconds)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                    <SmallStat label="Clubs total" value={statusData.stats.clubs_total} color="#FCD116" />
                    <SmallStat label="Clubs actifs" value={statusData.stats.clubs_active} color="#2E7D32" />
                    <SmallStat label="Clubs suspendus" value={statusData.stats.clubs_suspended} color="#F57F17" />
                    <SmallStat label="Clubs verrouillés" value={statusData.stats.clubs_locked} color="#C62828" />
                    <SmallStat label="Joueurs" value={statusData.stats.players} color="#1976D2" />
                    <SmallStat label="Employés" value={statusData.stats.employees} color="#7B1FA2" />
                    <SmallStat label="Transactions" value={statusData.stats.transactions} color="#0A6B3A" />
                    <SmallStat label="Membres" value={statusData.stats.members} color="#E8112D" />
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs tracking-overline text-white/55 mb-3">Composants</p>
                    <div className="space-y-2 text-sm">
                      <Component name="API" sub="FastAPI · 100% des endpoints répondent" />
                      <Component name="Base de données" sub="MongoDB connecté · indexes TTL & text actifs" />
                      <Component name="Stockage objets" sub="Emergent Object Storage" />
                      <Component name="Authentification" sub="bcrypt + sessions TTL 7j + rate-limit" />
                      <Component name="Planificateur" sub="APScheduler · 3 jobs actifs" />
                      <Component name="E-mails" sub="Resend API" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <div className="bg-[#161616] border border-white/10 rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <History className="w-4 h-4 text-white/55" />
                <p className="font-semibold text-sm">Dernières actions admin (30)</p>
              </div>
              {audit.length === 0 ? (
                <p className="px-4 py-12 text-center text-white/45 text-sm">Aucune action enregistrée.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {audit.map((a) => (
                    <div key={a.id} className="px-4 py-3 text-sm" data-testid={`audit-${a.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ActionBadge action={a.action} />
                          <span className="text-white/75 font-mono text-xs">{a.club_id?.slice(0, 8)}…</span>
                          {a.club_name && <span className="text-white">{a.club_name}</span>}
                        </div>
                        <span className="text-xs text-white/45">{new Date(a.created_at).toLocaleString("fr-FR")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Modifier le club</DialogTitle>
            <DialogDescription>Tout changement est consigné dans le journal d'audit.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom *</Label><Input required value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="admin-edit-name" /></div>
                <div><Label>Slug *</Label><Input required value={editForm.slug || ""} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} className="font-mono" data-testid="admin-edit-slug" /></div>
                <div><Label>Ville</Label><Input value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                <div><Label>Pays</Label><Input value={editForm.country || ""} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} /></div>
                <div><Label>Sport</Label><Input value={editForm.sport || ""} onChange={(e) => setEditForm({ ...editForm, sport: e.target.value })} /></div>
                <div><Label>Sous-titre</Label><Input value={editForm.subtitle || ""} onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Annuler</Button>
                <Button type="submit" className="bg-[#FCD116] hover:bg-[#E8BC0F] text-[#121212]" data-testid="admin-edit-submit">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={!!suspTarget} onOpenChange={(o) => !o && setSuspTarget(null)}>
        <DialogContent className="max-w-md" data-testid="admin-suspend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#F57F17]" /> Suspension temporaire
            </DialogTitle>
            <DialogDescription>
              Le club sera bloqué jusqu'à la date choisie, puis automatiquement réactivé. Toutes les sessions actives seront fermées.
            </DialogDescription>
          </DialogHeader>
          {suspTarget && (
            <form onSubmit={submitSuspend} className="space-y-3">
              <div className="bg-[#F57F17]/10 border border-[#F57F17]/30 rounded p-3 text-sm">
                Club concerné : <strong>{suspTarget.name}</strong>
              </div>
              <div>
                <Label>Date et heure de fin (UTC) *</Label>
                <Input type="datetime-local" required value={suspForm.until}
                  onChange={(e) => setSuspForm({ ...suspForm, until: e.target.value })}
                  data-testid="suspend-until" />
                <p className="text-[11px] text-white/55 mt-1">Le club retrouvera automatiquement son accès passée cette date.</p>
              </div>
              <div>
                <Label>Motif administratif *</Label>
                <Textarea required minLength={3} rows={3} value={suspForm.reason}
                  onChange={(e) => setSuspForm({ ...suspForm, reason: e.target.value })}
                  placeholder="Ex: Vérification des documents d'affiliation, mise à jour du dossier fiscal..."
                  data-testid="suspend-reason" />
                <p className="text-[11px] text-white/55 mt-1">Ce motif sera affiché au club lors de toute tentative de connexion.</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "+24h", h: 24 },
                  { label: "+7j", h: 24 * 7 },
                  { label: "+30j", h: 24 * 30 },
                ].map((p) => (
                  <Button key={p.label} type="button" variant="outline" size="sm"
                    onClick={() => {
                      const d = new Date(Date.now() + p.h * 3600 * 1000);
                      d.setSeconds(0, 0);
                      setSuspForm({ ...suspForm, until: d.toISOString().slice(0, 16) });
                    }}>{p.label}</Button>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSuspTarget(null)}>Annuler</Button>
                <Button type="submit" className="bg-[#F57F17] hover:bg-[#E65100] text-white" data-testid="suspend-submit-btn">
                  Suspendre
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#C62828] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Suppression définitive
            </DialogTitle>
            <DialogDescription>Cette action est <strong>irréversible</strong>. Toutes les données du club seront effacées.</DialogDescription>
          </DialogHeader>
          {delTarget && (
            <div className="space-y-3">
              <div className="bg-[#C62828]/10 border border-[#C62828]/30 rounded p-3 text-sm">
                Vous êtes sur le point de supprimer <strong>{delTarget.name}</strong> ({delTarget.stats?.players ?? 0} joueurs, {delTarget.stats?.teams ?? 0} équipes).
              </div>
              <div>
                <Label>Tapez <strong>{delTarget.name}</strong> pour confirmer :</Label>
                <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className="mt-2 font-mono" data-testid="admin-delete-confirm-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDelTarget(null); setDelConfirm(""); }}>Annuler</Button>
                <Button type="button"
                  onClick={confirmDelete}
                  disabled={delConfirm !== delTarget.name}
                  className="bg-[#C62828] hover:bg-[#B71C1C] text-white disabled:opacity-50"
                  data-testid="admin-delete-confirm-btn">
                  Supprimer définitivement
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Bulk delete dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o) { setReviewing(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="admin-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#C62828]">
              Refuser l&apos;inscription
            </DialogTitle>
            <DialogDescription>
              {reviewing && `Club : ${reviewing.name} (${reviewing.contact_email})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Motif du refus (sera envoyé par email au club)</Label>
            <textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex: Le récépissé fourni n'est pas lisible / n'est pas conforme à la décision officielle..."
              className="w-full border border-[#E0E0E0] rounded-md px-3 py-2 text-sm"
              data-testid="admin-reject-reason"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setReviewing(null); setRejectReason(""); }}>Annuler</Button>
              <Button type="button" onClick={rejectPending} className="bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="admin-reject-confirm">
                Confirmer le refus
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={bulkDelOpen} onOpenChange={(o) => { if (!o) { setBulkDelOpen(false); setBulkConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="admin-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#C62828] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Suppression en lot
            </DialogTitle>
            <DialogDescription>
              Cette action est <strong>irréversible</strong>. Toutes les données (joueurs, finances, documents, codes...) de ces clubs seront effacées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#C62828]/10 border border-[#C62828]/30 rounded p-3 text-sm max-h-48 overflow-y-auto">
              <p className="mb-2 font-semibold">{selected.size} club{selected.size > 1 ? "s" : ""} concerné{selected.size > 1 ? "s" : ""} :</p>
              <ul className="list-disc list-inside space-y-0.5 text-white/80 text-xs">
                {clubs.filter((c) => selected.has(c.id)).map((c) => (
                  <li key={c.id}>{c.name} <span className="text-white/45 font-mono">({c.slug})</span></li>
                ))}
              </ul>
            </div>
            <div>
              <Label>Tapez <strong className="font-mono">{BULK_CONFIRM_TOKEN}</strong> pour confirmer :</Label>
              <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} className="mt-2 font-mono" data-testid="admin-bulk-confirm-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setBulkDelOpen(false); setBulkConfirm(""); }}>Annuler</Button>
              <Button type="button"
                onClick={submitBulkDelete}
                disabled={bulkConfirm !== BULK_CONFIRM_TOKEN}
                className="bg-[#C62828] hover:bg-[#B71C1C] text-white disabled:opacity-50"
                data-testid="admin-bulk-confirm-btn">
                Supprimer {selected.size} club{selected.size > 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-[#161616] border border-white/10 rounded-md p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-overline text-white/55">{label}</p>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}1f`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-3xl font-extrabold">{value}</p>
    </div>
  );
}

function SmallStat({ label, value, color }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-md p-3">
      <p className="text-[10px] tracking-overline text-white/55">{label}</p>
      <p className="font-display text-2xl font-extrabold mt-1" style={{ color }}>{value ?? 0}</p>
    </div>
  );
}

function Component({ name, sub }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-1.5 h-1.5 rounded-full bg-[#2E7D32]" />
        <div>
          <p className="text-white font-medium text-sm">{name}</p>
          <p className="text-[11px] text-white/45">{sub}</p>
        </div>
      </div>
      <span className="text-[10px] tracking-overline text-[#2E7D32] font-medium">OPÉRATIONNEL</span>
    </div>
  );
}

function formatUptime(seconds) {
  if (!seconds || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}j`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}min`);
  return parts.join(" ");
}

function ActionBadge({ action }) {
  const map = {
    update_club: { color: "#1976D2", label: "Modification" },
    lock_club: { color: "#C62828", label: "Verrouillage" },
    unlock_club: { color: "#2E7D32", label: "Déverrouillage" },
    delete_club: { color: "#7B1FA2", label: "Suppression" },
    suspend_club: { color: "#F57F17", label: "Suspension" },
    lift_suspension: { color: "#2E7D32", label: "Levée suspension" },
    auto_lift_suspension: { color: "#2E7D32", label: "Auto-levée" },
    impersonate_club: { color: "#FCD116", label: "Accès support" },
  };
  const c = map[action] || { color: "#616161", label: action };
  return (
    <Badge variant="outline" style={{ backgroundColor: `${c.color}1f`, color: c.color, borderColor: `${c.color}66` }}>
      {c.label}
    </Badge>
  );
}
